import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1000/api';

const PUCForm = ({ onSubmit, role }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    mobile_number: '',
    vehicle_number: '',
    vehicle_type: 'bike',
    fuel_type: 'petrol',
    puc_expire_tenure: 6,
    amount: '',
    payment_method: 'cash',
    record_date: new Date().toLocaleDateString('en-CA', {
  timeZone: 'Asia/Kolkata'
})
  });

  const [isShiftClosed, setIsShiftClosed] = useState(false);

  useEffect(() => {
    const checkShiftStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/shift/${formData.record_date}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsShiftClosed(res.data.is_closed);
      } catch (err) {
        console.error('Failed to fetch shift status', err);
      }
    };
    checkShiftStatus();
  }, [formData.record_date]);

  const firstInputRef = useRef(null);

  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({
      ...formData,
      customer_name: '',
      mobile_number: '',
      vehicle_number: '',
      amount: '',
    });
    
    // Auto-focus the first field for the next fast entry
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  };

  return (
    <div className="dash-card">
      <form onSubmit={handleSubmit}>
        
        {/* Customer Section */}
        <div className="form-section">
          <h4 className="form-section-title">Customer Information</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="customer_name">Full Name</label>
              <input 
                type="text" 
                id="customer_name" 
                name="customer_name" 
                ref={firstInputRef}
                className="form-control"
                value={formData.customer_name}
                onChange={handleChange}
                placeholder="e.g. John Doe"
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="mobile_number">Mobile Number</label>
              <input 
                type="tel" 
                id="mobile_number" 
                name="mobile_number" 
                className="form-control"
                value={formData.mobile_number}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 10) handleChange({ target: { name: 'mobile_number', value: val } });
                }}
                placeholder="e.g. 9876543210"
                pattern="[0-9]{10}"
                maxLength="10"
                minLength="10"
                title="Must be exactly 10 digits"
                required 
              />
            </div>
          </div>
        </div>

        {/* Vehicle Section */}
        <div className="form-section">
          <h4 className="form-section-title">Vehicle Details</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="vehicle_number">Vehicle Number</label>
              <input 
                type="text" 
                id="vehicle_number" 
                name="vehicle_number" 
                className="form-control"
                value={formData.vehicle_number}
                onChange={handleChange}
                placeholder="e.g. MH 01 AB 1234"
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="vehicle_type">Vehicle Type</label>
              <select 
                id="vehicle_type" 
                name="vehicle_type" 
                className="form-control"
                value={formData.vehicle_type}
                onChange={handleChange}
                required
              >
                <option value="bike">Bike</option>
                <option value="car">Car</option>
                <option value="3 wheeler">3 Wheeler</option>
                <option value="truck">Truck</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="fuel_type">Fuel Type</label>
              <select 
                id="fuel_type" 
                name="fuel_type" 
                className="form-control"
                value={formData.fuel_type}
                onChange={handleChange}
                required
              >
                <option value="petrol">Petrol</option>
                <option value="diesel">Diesel</option>
                <option value="cng">CNG</option>
              </select>
            </div>
          </div>
        </div>

        {/* Transaction Section */}
        <div className="form-section">
          <h4 className="form-section-title">Transaction Details</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="puc_expire_tenure">PUC Tenure</label>
              <select 
                id="puc_expire_tenure" 
                name="puc_expire_tenure" 
                className="form-control"
                value={formData.puc_expire_tenure}
                onChange={handleChange}
                required
              >
                <option value="6">6 Months</option>
                <option value="12">12 Months</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="amount">Amount (₹)</label>
              <input 
                type="number" 
                id="amount" 
                name="amount" 
                className="form-control"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="payment_method">Payment Method</label>
              <select 
                id="payment_method" 
                name="payment_method" 
                className="form-control"
                value={formData.payment_method}
                onChange={handleChange}
                required
              >
                <option value="cash">Cash</option>
                <option value="paytm">Paytm / Online</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="record_date">Date</label>
              <input 
                type="date" 
                id="record_date" 
                name="record_date" 
                className="form-control"
                value={formData.record_date}
                onChange={handleChange}
                required 
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={isShiftClosed && role !== 'admin'} style={{ opacity: (isShiftClosed && role !== 'admin') ? 0.5 : 1 }}>
            {(isShiftClosed && role !== 'admin') ? 'Shift Closed for Date' : 'Save PUC Entry'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PUCForm;
